import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronsDownUp, ChevronsUpDown, Download, FileText, Plus } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { PatientTabFrame } from '@/components/patient/PatientTabFrame';
import { JournalExportModal } from '@/components/journal/JournalExportModal';
import { JournalHistoryFilters } from '@/components/journal/JournalHistoryFilters';
import { JournalTabNote, journalTabPractitionerName } from '@/components/journal/JournalTabNote';
import { useAuth } from '@/lib/auth';
import { useClinicMembers } from '@/hooks/useClinicMembers';
import {
  useExportPatientJournal,
  useJournalAmendmentCounts,
  useJournalEntries,
  type JournalEntryWithAuthor,
} from '@/hooks/useJournal';
import { useJournalAttachmentCounts } from '@/hooks/useJournalAttachments';
import { usePatientAppointments } from '@/hooks/usePatientAppointments';
import { useTreatmentCoursesPatientList } from '@/hooks/useTreatmentCourses';
import {
  JournalExportError,
  buildJournalExportPackage,
} from '@/lib/journal/buildJournalExportPackage';
import { fetchJournalAttachmentBytes } from '@/lib/journal/fetchJournalAttachment';
import { journalPdfLabelsFromTranslator } from '@/lib/journal/exportJournalPdf';
import {
  filterJournalExportPayloadToEntry,
  toJournalExportDocument,
} from '@/lib/journal/journalExportData';
import {
  JOURNAL_EXPORT_MODES,
  parseJournalExportMode,
  type JournalExportMode,
} from '@/lib/journal/journalExportMode';
import { courseContextByAppointmentId } from '@/lib/appointmentCourseContext';
import { courseAppointmentIdSet, patientJournalWritePath } from '@/lib/journalWritePath';
import { downloadBlob } from '@/lib/reporting/format';
import { parseCourseCivil, parseCourseInstant } from '@/lib/courseDateFormat';
import {
  filterJournalEntries,
  journalDisplayAppointment,
  resolveJournalDateRange,
  type JournalAttachmentFilter,
  type JournalDatePreset,
  type JournalStatusFilter,
  type JournalTypeFilter,
} from '@/utils/journalHistoryFilters';

const ICON = 1.75;

const DA_MONTH_HEADINGS = [
  'Januar',
  'Februar',
  'Marts',
  'April',
  'Maj',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'December',
] as const;

interface JournalSectionProps {
  clinicId: string;
  patientId: string;
  currentUserId: string | null | undefined;
  isAdmin: boolean;
  appointmentId?: string | null;
  entryId?: string | null;
  entryDate?: string | null;
}

function journalListMonthKey(entry: JournalEntryWithAuthor): string {
  const parts =
    parseCourseInstant(entry.encounter_at) ??
    parseCourseCivil(entry.entry_date) ??
    parseCourseInstant(entry.created_at);
  if (!parts) return '';
  return `${parts.year}-${parts.month}`;
}

function journalListMonthLabel(key: string): string {
  const [yearText, monthText] = key.split('-');
  const month = Number(monthText);
  const year = Number(yearText);
  if (!Number.isFinite(month) || !Number.isFinite(year) || month < 0 || month > 11) return '';
  return `${DA_MONTH_HEADINGS[month]} ${year}`;
}

export function JournalSection({
  clinicId,
  patientId,
  currentUserId,
  appointmentId,
  entryId,
}: JournalSectionProps) {
  const { t, i18n } = useTranslation();
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const canCreate = hasPermission(clinicId, 'journal', 'create');
  const canAmend = hasPermission(clinicId, 'journal', 'amend');
  const from = `${location.pathname}${location.search}`;

  const [search, setSearch] = useState('');
  const [datePreset, setDatePreset] = useState<JournalDatePreset>('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [typeFilter, setTypeFilter] = useState<JournalTypeFilter>('all');
  const [statusFilter, setStatusFilter] = useState<JournalStatusFilter>('all');
  const [attachmentFilter, setAttachmentFilter] = useState<JournalAttachmentFilter>('all');
  const [courseFilter, setCourseFilter] = useState<string>('all');
  const [practitionerId, setPractitionerId] = useState('');
  const [openIds, setOpenIds] = useState<Set<string>>(
    () => new Set(entryId && !appointmentId ? [entryId] : []),
  );
  const [showExportModal, setShowExportModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingNoteId, setExportingNoteId] = useState<string | null>(null);

  const dateRange = useMemo(
    () => resolveJournalDateRange(datePreset, customFrom, customTo, new Date()),
    [datePreset, customFrom, customTo],
  );
  const entriesQuery = useJournalEntries(clinicId, {
    patientId,
    includeDiscarded: statusFilter === 'discarded',
    from: dateRange.from,
    to: dateRange.to,
  });
  const exportJournal = useExportPatientJournal();
  const appointmentsQuery = usePatientAppointments(patientId);
  const coursesQuery = useTreatmentCoursesPatientList(clinicId, patientId);
  const members = useClinicMembers(clinicId);
  const memberRows = members.data ?? [];
  const entries = entriesQuery.data ?? [];
  const entryIds = useMemo(() => entries.map((entry) => entry.id), [entries]);
  const attachmentCountsQuery = useJournalAttachmentCounts(clinicId, entryIds);
  const attachmentCounts = attachmentCountsQuery.data ?? {};
  const amendmentCountsQuery = useJournalAmendmentCounts(entryIds);
  const amendmentCounts = amendmentCountsQuery.data ?? {};
  const appointmentById = useMemo(() => {
    return new Map((appointmentsQuery.data ?? []).map((row) => [row.id, row]));
  }, [appointmentsQuery.data]);
  const courseByAppointmentId = useMemo(
    () => courseContextByAppointmentId(coursesQuery.data ?? []),
    [coursesQuery.data],
  );

  useEffect(() => {
    if (!appointmentId) return;
    navigate(
      patientJournalWritePath(patientId, {
        appointmentId,
        entryId,
        source: 'journal',
        from,
      }),
      { replace: true },
    );
  }, [appointmentId, entryId, from, navigate, patientId]);

  useEffect(() => {
    if (appointmentId || !entryId) return;
    setOpenIds((current) => {
      if (current.has(entryId)) return current;
      const next = new Set(current);
      next.add(entryId);
      return next;
    });
  }, [appointmentId, entryId]);

  useEffect(() => {
    if (appointmentId || !entryId || entriesQuery.isLoading) return;
    const node = document.querySelector(`[data-journal-nav-id="${entryId}"]`);
    node?.scrollIntoView({ block: 'start' });
  }, [appointmentId, entriesQuery.isLoading, entryId]);

  const visibleEntries = useMemo(() => {
    const filtered = filterJournalEntries(entries, {
      search,
      status: statusFilter,
      type: typeFilter,
      attachment: attachmentFilter,
      attachmentCounts,
    });
    const courseIds =
      courseFilter === 'all'
        ? null
        : courseAppointmentIdSet(
            coursesQuery.data?.find((course) => course.id === courseFilter)?.steps ?? [],
          );
    return filtered.filter((entry) => {
      if (courseIds && (!entry.appointment_id || !courseIds.has(entry.appointment_id))) {
        return false;
      }
      if (practitionerId) {
        const appointment = entry.appointment_id
          ? appointmentById.get(entry.appointment_id)
          : undefined;
        if (entry.clinician_id !== practitionerId && appointment?.clinician_id !== practitionerId) {
          return false;
        }
      }
      return true;
    });
  }, [
    appointmentById,
    attachmentCounts,
    attachmentFilter,
    courseFilter,
    coursesQuery.data,
    entries,
    practitionerId,
    search,
    statusFilter,
    typeFilter,
  ]);

  const hasActiveFilters =
    Boolean(search) ||
    datePreset !== 'all' ||
    typeFilter !== 'all' ||
    statusFilter !== 'all' ||
    attachmentFilter !== 'all' ||
    courseFilter !== 'all' ||
    Boolean(practitionerId);

  const anyOpen = visibleEntries.some((entry) => openIds.has(entry.id));

  const runExport = async (selectedMode: JournalExportMode, noteId?: string) => {
    if (noteId) setExportingNoteId(noteId);
    else setExporting(true);
    try {
      const payload = await exportJournal.mutateAsync({
        patientId,
        exportMode: selectedMode,
      });
      const acceptedMode =
        parseJournalExportMode(
          payload && typeof payload === 'object' && 'export_mode' in payload
            ? (payload as { export_mode?: unknown }).export_mode
            : null,
        ) ?? selectedMode;
      const localeCode = i18n.language === 'da' ? 'da-DK' : 'en-GB';
      const scopedPayload = noteId ? filterJournalExportPayloadToEntry(payload, noteId) : payload;
      const exportDocument = toJournalExportDocument(scopedPayload, (type) =>
        t(`journal.type_${type}`),
      );
      const pack = await buildJournalExportPackage({
        document: exportDocument,
        fetchAttachment: fetchJournalAttachmentBytes,
        mode: acceptedMode,
        generatedAt: new Date(exportDocument.exportedAt),
        pdfOptions: {
          locale: localeCode,
          generatedAt: new Date(exportDocument.exportedAt),
          labels: journalPdfLabelsFromTranslator(t),
        },
      });
      downloadBlob(
        new Blob([new Uint8Array(pack.downloadBytes)], { type: pack.downloadMimeType }),
        pack.downloadFilename,
      );
      setShowExportModal(false);
      toast.success(t('journal.export_ready'));
    } catch (error) {
      toast.error(
        error instanceof JournalExportError && error.code === 'attachment_missing'
          ? t('journal.export_attachment_failed')
          : t('journal.export_failed'),
      );
    } finally {
      setExporting(false);
      setExportingNoteId(null);
    }
  };

  const handleExport = async (selectedMode: JournalExportMode) => {
    await runExport(selectedMode);
  };

  const openWrite = (entry?: JournalEntryWithAuthor, amend = false) => {
    navigate(
      patientJournalWritePath(patientId, {
        entryId: entry?.id,
        appointmentId: entry?.appointment_id,
        courseId: courseFilter !== 'all' ? courseFilter : undefined,
        history: courseFilter !== 'all' ? 'course' : 'all',
        amend,
        source: 'journal',
        from,
      }),
    );
  };

  const resetFilters = () => {
    setSearch('');
    setDatePreset('all');
    setCustomFrom('');
    setCustomTo('');
    setTypeFilter('all');
    setStatusFilter('all');
    setAttachmentFilter('all');
    setCourseFilter('all');
    setPractitionerId('');
  };

  const toggleNote = (id: string) => {
    setOpenIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllVisible = () => {
    if (anyOpen) {
      setOpenIds(new Set());
      return;
    }
    setOpenIds(new Set(visibleEntries.map((entry) => entry.id)));
  };

  const empty = !entriesQuery.isLoading && !entriesQuery.isError && entries.length === 0;
  const noMatches =
    !entriesQuery.isLoading &&
    !entriesQuery.isError &&
    entries.length > 0 &&
    visibleEntries.length === 0;

  const listItems = useMemo(() => {
    let lastMonthKey = '';
    return visibleEntries.map((entry) => {
      const monthKey = journalListMonthKey(entry);
      const monthLabel =
        monthKey && monthKey !== lastMonthKey ? journalListMonthLabel(monthKey) : null;
      if (monthKey) lastMonthKey = monthKey;
      return { entry, monthLabel };
    });
  }, [visibleEntries]);

  return (
    <PatientTabFrame
      headingId="journal-tab-title"
      title={t('patients.tabs.journal')}
      count={entries.length}
      action={
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setShowExportModal(true)}
            disabled={exporting || exportJournal.isPending}
            data-testid="journal-export"
          >
            <Download className="h-4 w-4" strokeWidth={ICON} />
            {t('journal.export')}
          </Button>
          {canCreate ? (
            <Button type="button" onClick={() => openWrite()}>
              <Plus className="h-4 w-4" strokeWidth={ICON} />
              {t('treatment_courses.write_note')}
            </Button>
          ) : null}
        </div>
      }
      error={entriesQuery.isError ? t('journal.load_error') : undefined}
      onRetry={() => void entriesQuery.refetch()}
      empty={
        empty
          ? {
              icon: <FileText className="h-7 w-7" />,
              title: t('journal.no_entries'),
              description: t('journal.empty_hint'),
            }
          : undefined
      }
    >
      <section data-testid="journal-section">
        <JournalHistoryFilters
          search={search}
          onSearchChange={setSearch}
          datePreset={datePreset}
          onDatePresetChange={setDatePreset}
          customFrom={customFrom}
          customTo={customTo}
          onCustomRangeChange={({ from: nextFrom, to }) => {
            setCustomFrom(nextFrom);
            setCustomTo(to);
          }}
          type={typeFilter}
          onTypeChange={setTypeFilter}
          status={statusFilter}
          onStatusChange={setStatusFilter}
          attachment={attachmentFilter}
          onAttachmentChange={setAttachmentFilter}
          courseValue={courseFilter}
          onCourseChange={setCourseFilter}
          courses={(coursesQuery.data ?? []).map((course) => ({
            value: course.id,
            label: course.template_name,
          }))}
          practitionerValue={practitionerId}
          onPractitionerChange={setPractitionerId}
          practitioners={memberRows.map((member) => ({
            value: member.user_id,
            label: member.name,
          }))}
        />

        {entriesQuery.isLoading ? (
          <div aria-hidden>
            {Array.from({ length: 4 }, (_, index) => (
              <div key={index} className="jf-sk">
                <div className="dt" />
                <div>
                  <div className="t" />
                  <div className="m" />
                  <div className="p" />
                  <div className="p" />
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {!entriesQuery.isLoading && !empty ? (
          <>
            <div className="jf-meta">
              <span className="num">
                {t('journal.list_count', { count: visibleEntries.length })}
              </span>
              {visibleEntries.length > 0 ? (
                <div className="r">
                  <button type="button" className="jf-tbtn" onClick={toggleAllVisible}>
                    {anyOpen ? (
                      <ChevronsDownUp className="h-4 w-4" strokeWidth={ICON} aria-hidden />
                    ) : (
                      <ChevronsUpDown className="h-4 w-4" strokeWidth={ICON} aria-hidden />
                    )}
                    {anyOpen ? t('journal.collapse_all') : t('journal.expand_all')}
                  </button>
                </div>
              ) : null}
            </div>

            {noMatches ? (
              <div className="jf-empty-filter">
                {t('journal.no_matches')}
                {hasActiveFilters ? (
                  <>
                    {' '}
                    <Button type="button" variant="link" onClick={resetFilters}>
                      {t('journal.reset_filters')}
                    </Button>
                  </>
                ) : null}
              </div>
            ) : (
              <ol className="jf-l" aria-label={t('journal.notes_list')}>
                {listItems.map(({ entry, monthLabel }) => {
                  const appointment = entry.appointment_id
                    ? appointmentById.get(entry.appointment_id)
                    : undefined;
                  const linked = journalDisplayAppointment(entry, appointment ?? null);
                  const course = entry.appointment_id
                    ? courseByAppointmentId.get(entry.appointment_id)
                    : undefined;
                  return (
                    <JournalTabMonthGroup key={entry.id} monthLabel={monthLabel}>
                      <JournalTabNote
                        entry={entry}
                        patientId={patientId}
                        clinicId={clinicId}
                        appointment={appointment ?? null}
                        courseName={course?.courseName}
                        courseId={course?.courseId}
                        stepId={course?.stepId}
                        stepIndex={course?.stepIndex}
                        stepCount={course?.stepCount}
                        practitionerName={journalTabPractitionerName(entry, linked, memberRows)}
                        members={memberRows}
                        currentUserId={currentUserId}
                        open={openIds.has(entry.id)}
                        onToggle={() => toggleNote(entry.id)}
                        attachmentCount={attachmentCounts[entry.id] ?? 0}
                        amendmentCount={amendmentCounts[entry.id] ?? 0}
                        canCreate={canCreate}
                        canAmend={canAmend}
                        exporting={exportingNoteId === entry.id}
                        onContinue={() => openWrite(entry)}
                        onCorrect={() => openWrite(entry, true)}
                        onExportNote={() =>
                          void runExport(JOURNAL_EXPORT_MODES.journal_with_bilag, entry.id)
                        }
                      />
                    </JournalTabMonthGroup>
                  );
                })}
              </ol>
            )}
          </>
        ) : null}
      </section>
      {showExportModal ? (
        <JournalExportModal
          busy={exporting || exportJournal.isPending}
          onClose={() => setShowExportModal(false)}
          onConfirm={(mode) => void handleExport(mode)}
        />
      ) : null}
    </PatientTabFrame>
  );
}

function JournalTabMonthGroup({
  monthLabel,
  children,
}: {
  monthLabel: string | null;
  children: ReactNode;
}) {
  return (
    <>
      {monthLabel ? (
        <li className="jf-mo" role="presentation">
          {monthLabel}
        </li>
      ) : null}
      {children}
    </>
  );
}
